import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { GridColDef, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { Button, Stack, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers-pro';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { SERVICES, USER_SERVICE } from '@shared/constants/apiConfig';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { WrapContent, Wrapper } from '@styles/CommonStyles';
import { useLeadSource } from '@shared/hooks/useLeadSource';
import { SaveButton } from '@shared/inputs/Buttons';
import { RoleModal } from '../Roles/RoleModal/RoleModal';
import { IAssignments } from '../Roles';
import { IGroupOfUsers } from '../Roles/interfaces';
import { IUser } from '../Users/interfaces';
import { getHighestRank, getSourcesColumnData, updateRowRank } from './utils';
import { DripFeederModal } from './components/DripFeederModal/DripFeederModal';
import { initialDripFeederValues } from './constants';
import {
  requestCreateDripFeeder,
  requestDeleteDripFeeder,
  requestUpdateDripFeeder,
  requestUpdateGlobalSettings,
} from './request';
import { IDripFeeder, ISources } from './types';

type DripFeederProps = {
  fetchedDripFeeders?: IDripFeeder[];
  mutateDripFeeders?: () => void;
  isLoading: boolean;
};

export const DripFeeder = ({ fetchedDripFeeders = [], mutateDripFeeders, isLoading }: DripFeederProps) => {
  const { leadSourceData: fetchedSources } = useLeadSource<ISources[]>();
  const { data: fetchedGlobalSetting } = useSWR<{ id: string; time: string }>(`${SERVICES.USER}/dripFeeder/dripFeederGlobalSetting`);

  const { data: allUsers = [] } = useSWR<IUser[]>(`${SERVICES.USER}/users`);
  const { data: groupOfUsers } = useSWR<IGroupOfUsers>(`${SERVICES.USER}/roleAssignment/groupOfUsers`);

  const [currentDripFeeder, setCurrentDripFeeder] = useState<IDripFeeder | null>(null);
  const [dripFeederModalPosition, setDripFeederModalPosition] = useState(0);
  const [currentRoleId, setCurrentRoleId] = useState<string | null>(null);
  const [roleModalPosition, setRoleModalPosition] = useState(0);

  const [isEditMode, setIsEditMode] = useState(true);
  const [selectedDate, handleDateChange] = useState<Date | null>(null);
  const [globalSettingsId, setGlobalSettingsId] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: assignments, mutate: mutateAssignments } = useSWR<IAssignments>(
    !!currentRoleId && `${USER_SERVICE.EDIT_USERS}/${currentRoleId}`
  );

  useEffect(() => {
    if (fetchedGlobalSetting) {
      setGlobalSettingsId(fetchedGlobalSetting.id);
      handleDateChange(new Date(fetchedGlobalSetting.time));
    }
  }, [fetchedGlobalSetting]);

  const handleAddButtonClick = useCallback(() => {
    setIsEditMode(false);
    const rank = getHighestRank(fetchedDripFeeders);
    setCurrentDripFeeder({ ...initialDripFeederValues, rank });
    setDripFeederModalPosition(prevState => prevState + 1);
  }, [fetchedDripFeeders, initialDripFeederValues]);

  const handleSubmit = useCallback(async (data: IDripFeeder) => {
    setLoading(true);
    if (isEditMode) {
      setCurrentDripFeeder(null);
      setDripFeederModalPosition(0);
      await requestUpdateDripFeeder(data);
    } else {
      setCurrentDripFeeder(null);
      setDripFeederModalPosition(0);
      await requestCreateDripFeeder(data);
    }
    mutateDripFeeders?.();
    setLoading(false);
  }, [isEditMode]);

  const handleRowOrderChange = useCallback(async (params: GridRowOrderChangeParams) => {
    if (params.oldIndex !== params.targetIndex) {
      setLoading(true);
      await updateRowRank(params, fetchedDripFeeders).then(() => mutateDripFeeders?.());
      setLoading(false);
    }
  }, [fetchedDripFeeders]);

  const handleCloseDripFeederModal = useCallback(() => {
    setCurrentDripFeeder(null);
    setDripFeederModalPosition(0);
  }, []);

  const gridActions = useMemo(() => ({
    onEdit: (data: IDripFeeder) => {
      setCurrentDripFeeder(data);
      setDripFeederModalPosition(prevState => prevState + 1);
      setIsEditMode(true);
    },
    onDelete: (data: IDripFeeder) => requestDeleteDripFeeder(data),
  }), []);

  const columns: GridColDef[] = useMemo(() => ([
    dataGridCol('rank', {
      flex: 1,
      minWidth: 60,
    }),
    dataGridCol('name', {
      flex: 2,
      minWidth: 160,
    }),
    dataGridCol('sources', {
      flex: 4,
      minWidth: 350,
      renderCell: ({ row: { sources } }) => {
        const { rankedStr, otherCount, ignoredCount } = getSourcesColumnData(sources, fetchedSources ?? []);

        return (
          <WrapContent>
            <Typography>Ranked: {rankedStr || 'None'}</Typography>
            <Typography>{`${otherCount} Other & ${ignoredCount} Ignored`}</Typography>
          </WrapContent>
        );
      },
    }),
    dataGridCol('maxPulls', {
      flex: 1,
      minWidth: 100,
    }),
    dataGridCol('minutesBetweenPulls', {
      flex: 1,
      minWidth: 100,
    }),
    dataGridCol('userCount', {
      flex: 1,
      minWidth: 100,
    }),
    dataGridCol('', {
      flex: 1,
      minWidth: 100,
      headerName: 'Edit users',
      sortable: false,
      renderCell: ({ row: { roleId } }) => (
        <Button
          variant={'contained'}
          size='small'
          onClick={() => {
            setCurrentRoleId(roleId);
            setRoleModalPosition(prevState => prevState + 1);
          }}
        >
          Edit Users
        </Button>
      ),
    }),
  ]), [fetchedSources]);

  const subHeader = (
    <Stack
      flexDirection="row"
      alignItems="center"
      alignSelf="flex-end"
      gap={2}
      mb={2}
      mt={1}
    >
      <TimePicker
        ampm={false}
        minutesStep={30}
        label="Reset time for pull counts"
        value={selectedDate}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock,
        }}
        // renderInput={(props) => <TextField {...props} />}
        onChange={(value: Date | null) => handleDateChange(value)}
      />
      <SaveButton
        variant="contained"
        width='80px'
        onClick={() => {
          if (selectedDate) requestUpdateGlobalSettings({ id: globalSettingsId, time: selectedDate?.toISOString() });
        }}
      />
    </Stack>
  );

  return (
    <Wrapper>
      {!!currentDripFeeder && (
        <DripFeederModal
          isEditMode={isEditMode}
          dripFeederModalPosition={dripFeederModalPosition}
          handleSubmit={handleSubmit}
          handleClose={handleCloseDripFeederModal}
          currentDripFeeder={currentDripFeeder}
          fetchedSources={fetchedSources}
        />
      )}

      {!!roleModalPosition && (
        <RoleModal
          roleModalPosition={roleModalPosition}
          roleName="Drip Feeder"
          roleId={String(currentRoleId)}
          allUsers={allUsers}
          users={assignments?.rolesUsers}
          groupOfUsers={groupOfUsers}
          handleClose={() => {
            setCurrentRoleId(null);
            setRoleModalPosition(0);
            mutateAssignments();
          }}
        />
      )}

      <LoaderBackdrop open={loading} />
      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        disableSelectionOnClick
        showClearAllFilter
        rowReordering
        name="Drip Feeder"
        pageSize={15}
        columns={columns}
        loading={isLoading}
        rows={isLoading ? [] : fetchedDripFeeders}
        actions={gridActions}
        getRowHeight={() => 'auto'}
        buttonTitle="Add Drip Feeder Set"
        withSubheader={subHeader}
        onButtonClick={handleAddButtonClick}
        onRowOrderChange={handleRowOrderChange}
      />
    </Wrapper>
  );
};
